import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { Text } from '../text'

export type ParticipantItemProps = {
    /** Leading 44px status/role icon (a `UnitTaskTypeIcon` or an avatar image). */
    icon?: React.ReactNode
    /** Primary line — participant name. */
    name: string
    /** Secondary line (Figma "Head of APOC"). */
    subtitle?: string
    /** Tertiary muted line (Figma "Observer" — the invitation role/status). */
    status?: string
    /** Trailing slot — arrival-time box, the more menu, etc. */
    trailing?: React.ReactNode
    className?: string
    testID?: string
}

/**
 * Unit Task List participant row (Figma `UnitTaskListParticipantsItem`, node
 * 10786:97054): a leading role icon, then name / subtitle / muted status, with
 * an optional trailing slot. Presentational — the live `ChannelParticipant`
 * maps invitation data to these props and supplies the icon.
 */
export const ParticipantItem: React.FC<ParticipantItemProps> = ({
    icon,
    name,
    subtitle,
    status,
    trailing,
    className,
    testID,
}) => (
    <Box
        className={twMerge('flex-row flex-wrap items-center gap-3 py-3', className)}
        testID={testID}
    >
        {icon}
        {/* `basis-24` is load-bearing: with `flex-1 min-w-0` alone this column shrinks
            to zero in a narrow panel and the name paints over the trailing slot, so the
            basis makes the row wrap instead (AOS-1169). */}
        <Box className='min-w-0 flex-1 basis-24 overflow-hidden'>
            <Text size='body-lg' tone='default'>
                {name}
            </Text>
            {subtitle ? (
                <Text size='body' tone='default'>
                    {subtitle}
                </Text>
            ) : null}
            {status ? (
                <Text size='caption' tone='muted'>
                    {status}
                </Text>
            ) : null}
        </Box>
        {trailing}
    </Box>
)

ParticipantItem.displayName = 'ParticipantItem'
